import { useCallback, useMemo, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '@app/store';
import { setSelectedProducts, clearSelectedProducts, clearUnsavedChanges } from '@store/slices/shop';
import { useDraftPersistence } from '@/hooks/useDraftPersistence';

/**
 * Dünner Wrapper um die generische Draft-Persistierung.
 * Ersetzt die alte, monolithische Implementierung vollständig.
 */
export const useCheckoutPersistence = () => {
  const dispatch = useDispatch();
  const selectedProducts = useSelector((state: RootState) => state.shop.selectedProducts);
  const hasUnsavedChanges = useSelector((state: RootState) => state.shop.hasUnsavedChanges);

  // Stable snapshot function – only changes when selectedProducts changes
  const snapshotFn = useCallback(() => selectedProducts, [selectedProducts]);

  // Generische Persistierung unter der festen Kategorie "CHECKOUT"
  const {
    hasRestoredState,
    isRestoring,
    isSaving,
    clearDraft,
  } = useDraftPersistence(
    'CHECKOUT',
    snapshotFn,
    (saved) => dispatch(setSelectedProducts(saved)),
  );

  // Once a save has completed, clear the unsaved flag so we do not attempt
  // to persist unchanged data again.
  useEffect(() => {
    if (!isSaving && hasUnsavedChanges) {
      dispatch(clearUnsavedChanges());
    }
  }, [isSaving, hasUnsavedChanges, dispatch]);

  /**
   * Verwirft NUR den gespeicherten Entwurf auf dem Server (lokaler State bleibt).
   * Nutzung: User klickt im Resume-Prompt auf "Auswahl löschen".
   */
  const clearSavedState = useCallback(async () => {
    await clearDraft();
    dispatch(clearSelectedProducts());
  }, [clearDraft, dispatch]);

  /**
   * Löscht Entwurf + lokale Auswahl (nach erfolgreichem Checkout).
   */
  const clearCheckoutState = useCallback(async () => {
    await clearDraft();
    dispatch(clearSelectedProducts());
  }, [clearDraft, dispatch]);

  return {
    hasRestoredState,
    isRestoring,
    isSaving,
    clearSavedState,
    clearCheckoutState,
  } as const;
}; 